import { PageLayout } from '@/global/components';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { FC, useEffect } from 'react';
import { CREATE_DATA_SOURCE_SFTP, CREATE_DATA_SOURCE_SFTP_STEPS } from './constants';
import { NameAndBucketStep } from './steps/NameAndBucketStep';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useHistory } from 'react-router-dom';
import { SFTPConfigurationCompletedStep, SFTPCSVHeaderStep } from './steps';
import { FileDataSourceColumnMapStep, FileDataTypeStep } from '../file/steps';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { DataSource, DataSourceIDEnum, DataSourceSFTPConfig } from '@/models/dataSource';
import { Loadable } from '@/components/loaders';

type CreateDataSourceSFTPContent = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourceSFTPContent: FC<CreateDataSourceSFTPContent> = ({ dataSourceId, modalProps }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(CREATE_DATA_SOURCE_SFTP_STEPS);

  const { isFetching, data: sources } = useGetDataSourceTypesQuery();
  const dataSourceType = sources?.find((source) => source.id === dataSourceId);
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceSFTPConfig>> | undefined;

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_SFTP.NAME_AND_BUCKET:
        return <NameAndBucketStep onNextStep={handleNextStep} onCancel={handleCancel} dataSourceType={dataSourceType} />;

      case CREATE_DATA_SOURCE_SFTP.HEADER:
        return <SFTPCSVHeaderStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_SFTP.DATA_TYPE:
        return <FileDataTypeStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_SFTP.MAP_COLUMNS:
        return <FileDataSourceColumnMapStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_SFTP.INSTALLATION_SUCCESS:
        return (
          <SFTPConfigurationCompletedStep
            onInit={completeCurrentStep}
            onNextStep={handleCloseAndContinue}
            onPrevStep={handleCloseOnly}
            isModal={!!modalProps}
          />
        );

      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />
      <Loadable isLoading={isFetching}>{stepTemplate()}</Loadable>
    </PageLayout>
  );
};
